/**
 * Retrieve the top N items from a specified column in a CSV file.
 *
 * @param {string} data The data array from a CSV file.
 * @param {number} n The number of top items to retrieve.
 * @param {string} target_col The name of the column containing JSON data with the items.
 * @return {<Array>} An array of the top N items, sorted by count in descending order.
 */
export async function top_n_data(data, n, target_col, count_item) {
    try {
        let col_obj = JSON.parse(data[0][target_col]);
        let topN;

        if (typeof col_obj === 'object' && !Array.isArray(col_obj)) {
            // Sorting the object based on values and extracting top N
            topN = Object.entries(col_obj).sort((a, b) => b[1] - a[1]).slice(0, n);
        } else if (Array.isArray(col_obj)) {
            // Sorting the array of objects based on the 'count' property and extracting top N
            topN = col_obj.sort((a, b) => b[count_item] - a[count_item]).slice(0, n);
        } else {
            // Handle other types if necessary, or throw an error
            throw new Error('Invalid input: col_obj must be an object or an array of objects.');
        }
        return topN;
    } catch (error) {
    }
}

/**
 * Calculates the total count of items in the data array.
 *
 * @param {Array} data - The data array.
 * @returns {number} - The total count of items.
 */
export function total_count(data) {
    return data.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.count;
    }, 0);
}

/**
 * Retrieves the number of datasets from the data array.
 *
 * @param {Array} data - The data array.
 * @param {number} date_index - The index of the date in the data array.
 * @returns {number} - The number of datasets.
 */
export async function num_of_dataset(data, date_index) {
    try {
        const num_of_data = data[date_index]['num_datasets'];
        return num_of_data;
    } catch (error) {
        throw error;
    }
}

/**
 * Calculates the number of objects and arrays in a specified column of the data array.
 *
 * @param {Array} daily_fetch_data - The data array fetched daily.
 * @param {string} target_col - The name of the target column.
 * @returns {Array} - An array of counts of objects and arrays.
 */
export function num_of_obj_and_arr(daily_fetch_data, target_col) {
    let num_arr = [];
    let cur_count = 0;

    for (let dataset of daily_fetch_data) {
        const cur_dataset_str = dataset[target_col]; //string

        // if data_info is not empty
        if (cur_dataset_str !== 'NaN') {
            const cur_dataset = JSON.parse(dataset[target_col]);

            // Array
            if (Array.isArray(cur_dataset)) {
                cur_count = cur_dataset.length;
            } 
            // Object (organization)
            else if (typeof cur_dataset === 'object') {
                cur_count = 1;
                if (cur_dataset === null) cur_count = 0;
            } 
            // Number (num_resources)
            else if (typeof cur_dataset === 'number') {
                cur_count = cur_dataset;
            }
            // String (license_id)
            else if (typeof cur_dataset === 'string') {
                cur_count = 1;
                if (cur_dataset === 'notspecified') cur_count = 0;
            }
        }
        
        num_arr.push(cur_count);
    }

    return num_arr;
}

/**
 * Generates an array of ratio objects for a specified target column.
 *
 * @param {Object} data - The data object.
 * @param {Object} mapping - The mapping table for column names.
 * @param {string} target_col - The name of the target column.
 * @returns {Array} - An array of ratio objects with display_name and value properties.
 */
export function gen_ratio_arr(data, mapping, target_col) {
    let ratio_arr = []

    for (let column in mapping) {
        let cur_dict = {}
        let field_name = mapping[column];
        let num = JSON.parse(data[`${target_col}_filled_stats`])[column];
        let total = JSON.parse(data[`num_${target_col}`]);
        let ratio = num / total

        cur_dict['key'] = column;
        cur_dict['display_name'] = field_name;
        cur_dict['value'] = ratio;

        ratio_arr.push(cur_dict)
    }
    return ratio_arr;
}